import React, { useState } from 'react';
import {
  Building2, User, KeyRound, Crown, Presentation, ArrowRight, Lock, LayoutGrid, Handshake, PlayCircle,
} from 'lucide-react';
import { AppRole } from './shared/theme';
import { ceoApiConfigured } from './ceo/ceoApi';
import { isDemoEntryVisible } from './demo/demoMode';
import { TERMS_URL, PRIVACY_URL, openPolicy } from './shared/legal';
import ServicesShowcase from './ServicesShowcase';
import AppTour from './AppTour';
import { useLanguage, LanguageSelector } from '../components/LanguageContext';
import './shared/portals.css';

interface RoleSelectScreenProps {
  onSelect: (role: AppRole) => void;
}

// Corporate design allows only #D6B160 / #FFFFFF / #000000, so the roles are
// ranked by treatment rather than colour: gold-filled (primary), gold-outlined
// (secondary), ink-outlined (tertiary).
type Tone = 'filled' | 'outlined' | 'plain';

// `title` / `subtitle` hold translation keys, resolved with t() at render time.
const ROLES: {
  role: AppRole;
  title: string;
  subtitle: string;
  icon: React.ComponentType<any>;
  tone: Tone;
  iconColor: string;
}[] = [
  {
    role: 'owner',
    title: 'rsOwnerTitle',
    subtitle: 'rsOwnerSub',
    icon: Building2,
    tone: 'filled',
    iconColor: '#000000',
  },
  {
    role: 'tenant',
    title: 'rsTenantTitle',
    subtitle: 'rsTenantSub',
    icon: KeyRound,
    tone: 'outlined',
    iconColor: '#B99A4C',
  },
  {
    role: 'guest',
    title: 'rsGuestTitle',
    subtitle: 'rsGuestSub',
    icon: User,
    tone: 'plain',
    iconColor: '#3F3B34',
  },
  // Cooperation / Referral Partner — used to sit inside the Property Owner
  // menu. It is its own way in now, but NOT its own account type: it signs in
  // with the same InvestMenter login and shares that session, so an owner who
  // is already signed in goes straight through. See portals/partner.
  {
    role: 'partner',
    title: 'rsPartnerTitle',
    subtitle: 'rsPartnerSub',
    icon: Handshake,
    tone: 'outlined',
    iconColor: '#B99A4C',
  },
  // Code-gated showcase for management: one access code, then all four views
  // (Owner / Tenant / CEO / Airbnb Guest) on sample data. Distinct from the
  // Airbnb "Guest" row above — that one books a stay; this one demonstrates
  // the product. See portals/demo/DemoShell.
  {
    role: 'demo',
    title: 'rsDemoTitle',
    subtitle: 'rsDemoSub',
    icon: Presentation,
    tone: 'plain',
    iconColor: '#3F3B34',
  },
  // Company-side executive access. Backed by the CRM SaaS client portal
  // (crm-saas /portal/ceo), not by the property-management backend.
  {
    role: 'ceo',
    title: 'rsCeoTitle',
    subtitle: 'rsCeoSub',
    icon: Crown,
    tone: 'plain',
    iconColor: '#B8933F',
  },
];

// Without the company portal configured, the CEO tab can only show SAMPLE
// figures for a placeholder company. Those must never reach real users — a
// stranger's investors tapping "CEO" and seeing plausible-looking accounts
// would read as real. So the row is hidden in production until the CRM SaaS
// endpoint is wired up; local/preview builds still show it for review.
const isProd = process.env.NODE_ENV === 'production';
const VISIBLE_ROLES = ROLES.filter((r) => {
  // Sample-figure CEO tab stays hidden until the CRM endpoint is wired up.
  if (r.role === 'ceo') return ceoApiConfigured() || !isProd;
  // Showcase is a staff tool: on the live portal it appears only via
  // ?demo=1 / #demo, so investors signing in never see it. See isDemoEntryVisible.
  if (r.role === 'demo') return isDemoEntryVisible();
  return true;
});

// FIRST RELEASE SCOPE. The launch ships the two portals that are finished end
// to end — Property Owner and Tenant. Short Stay Guest has no booking backend
// behind it yet, the Partner programme is still onboarding its first
// cooperators, the CEO console is company-side rather than customer-side, and
// "What we offer" is the sales catalogue that goes with them.
//
// Hidden in every PRODUCTION build — the Play Store app and portal.investmenter.de
// alike — so the screen a store reviewer sees is the screen on the web. Add a
// role to LAUNCH_ROLES to release it; the rows are hidden, never removed.
const LAUNCH_ROLES: AppRole[] = ['owner', 'tenant'];

// Staff escape hatch, the same shape as the showcase's ?demo=1: the hidden rows
// come back on `?roles=all` or `#roles`. Reachable on the web, where Partner and
// CEO are still worked on, and not in the app, which has no address bar. Local
// dev always shows everything.
function showAllRoles(): boolean {
  if (!isProd) return true;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('roles') === 'all') return true;
    return window.location.hash.replace('#', '').toLowerCase() === 'roles';
  } catch {
    return false;
  }
}

const RoleSelectScreen: React.FC<RoleSelectScreenProps> = ({ onSelect }) => {
  const { t } = useLanguage();

  // Read at render, so the URL hatch is honoured on the screen the visitor
  // actually landed on.
  const fullEntry = showAllRoles();
  const roles = VISIBLE_ROLES.filter(
    // The showcase gates itself on ?demo=1 already, so the launch scope leaves
    // it alone rather than gating it twice.
    (r) => fullEntry || r.role === 'demo' || LAUNCH_ROLES.includes(r.role)
  );

  // Two sheets that take over the screen rather than becoming extra role rows
  // — a visitor still has to come back here and choose how to continue:
  //
  //   "What we offer" — the browsable services catalogue.
  //   The walkthrough  — the film. Unlike the catalogue it ships in PRODUCTION
  //                      and is NOT gated by LAUNCH_ROLES: an invited owner who
  //                      has never seen the app is exactly who it is for, so it
  //                      must be there on the store build and the live portal.
  //                      "Continue to sign in" hands straight to the owner
  //                      portal, where the tour points the whole way through.
  //
  // BOTH hooks are declared before either early return. Putting the second one
  // after `if (showServices) return …` calls it conditionally, which is a
  // rules-of-hooks error and breaks the build.
  const [showServices, setShowServices] = useState<false | 'home' | 'pm'>(false);
  const [showTour, setShowTour] = useState(false);

  if (showServices) {
    return (
      <ServicesShowcase
        initialTab={showServices === 'pm' ? 'pm' : 'home'}
        onClose={() => setShowServices(false)}
      />
    );
  }
  if (showTour) {
    return (
      <AppTour
        onClose={() => setShowTour(false)}
        onStart={() => {
          setShowTour(false);
          onSelect('owner');
        }}
        /* The management detail lives in the showcase's PM tab. Reached from
           here it stays reachable in PRODUCTION even though the "What we
           offer" row itself is out of launch scope — the tour is the only way
           in, and management is the thing we most need a prospect to read. */
        onSeePm={() => {
          setShowTour(false);
          setShowServices('pm');
        }}
      />
    );
  }

  return (
    <div className="pt-screen rs-screen">
      <div className="rs-frame" aria-hidden="true" />

      <div className="rs-content">
        {/* Language switch on the very first screen: a DACH investor should be
            able to choose Deutsch before signing in, not after. */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <LanguageSelector compact />
        </div>

        <header className="rs-head">
          <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="rs-logo" />

          <div className="rs-rule" aria-hidden="true">
            <span />
            <i>&#9670;</i>
            <span />
          </div>

          <h1 className="rs-title">{t('rsWelcome')}</h1>
          <p className="rs-sub">{t('rsChooseHow')}</p>
        </header>

        <div className="rs-list">
          {roles.map(({ role, title, subtitle, icon: Icon, tone, iconColor }) => (
            <button
              key={role}
              onClick={() => onSelect(role)}
              className={`rs-row rs-row--${tone}`}
            >
              <span className="rs-mark">
                <Icon size={19} color={iconColor} strokeWidth={1.9} />
              </span>

              <span className="rs-row-text">
                <span className="rs-row-title">{t(title)}</span>
                <span className="rs-row-sub">{t(subtitle)}</span>
              </span>

              <ArrowRight className="rs-arrow" size={16} strokeWidth={1.8} />
            </button>
          ))}
        </div>

        {/* Not a role — a film. Sits directly under the role rows because the
            question it answers ("what am I signing in to?") comes before the
            choice, and gold-marked so it reads as an invitation rather than a
            third account type. Always visible, including in production. */}
        <div className="rs-list rs-offer">
          <button className="rs-row rs-row--outlined" onClick={() => setShowTour(true)}>
            <span className="rs-mark">
              <PlayCircle size={19} color="#B99A4C" strokeWidth={1.9} />
            </span>

            <span className="rs-row-text">
              <span className="rs-row-title">{t('rsTourTitle')}</span>
              <span className="rs-row-sub">{t('rsTourSub')}</span>
            </span>

            <ArrowRight className="rs-arrow" size={16} strokeWidth={1.8} />
          </button>
        </div>

        {/* Separate sheet, so it reads as an aside to the role choice rather
            than a fifth way to sign in. Out of scope for the first release —
            see LAUNCH_ROLES. */}
        {fullEntry && (
          <div className="rs-list rs-offer">
            <button className="rs-row rs-row--outlined" onClick={() => setShowServices('home')}>
              <span className="rs-mark">
                <LayoutGrid size={19} color="#B99A4C" strokeWidth={1.9} />
              </span>

              <span className="rs-row-text">
                <span className="rs-row-title">{t('rsServicesTitle')}</span>
                <span className="rs-row-sub">{t('rsServicesSub')}</span>
              </span>

              <ArrowRight className="rs-arrow" size={16} strokeWidth={1.8} />
            </button>
          </div>
        )}

        <footer className="rs-footer">
          <Lock size={11} strokeWidth={2} />
          <span>{t('rsSecurityNote')}</span>
        </footer>

        {/*
          Policy links, reachable before any login so they're available without
          an account. Deliberately NOT worded as "by continuing you agree…":
          explicit, versioned consent is captured at sign-up (see POLICY_VERSION
          in InvestorPortal), and implying consent here would undercut that
          audit trail rather than strengthen it.
        */}
        <nav className="rs-legal" aria-label={t('rsLegal')}>
          <a href={TERMS_URL} onClick={openPolicy(TERMS_URL)} target="_blank" rel="noopener noreferrer">
            {t('rsTerms')}
          </a>
          <span className="rs-legal-sep" aria-hidden="true">&middot;</span>
          <a href={PRIVACY_URL} onClick={openPolicy(PRIVACY_URL)} target="_blank" rel="noopener noreferrer">
            {t('rsPrivacy')}
          </a>
        </nav>

        {/*
          NOTE: EA / staff is intentionally NOT a button here.
          Staff log in through the normal Owner / Tenant login;
          when their account is flagged as staff/admin, their overview
          renders in Admin mode (see EAAdminConsole).
        */}
      </div>
    </div>
  );
};

export default RoleSelectScreen;
